import React from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  EnhancedMultiSelect,
} from "@/components/ui/enhanced-multiselect";
import { Label } from "@/components/ui/label";
import type { FilterValue } from "../types";
import { useLingui } from "@lingui/react/macro";
import { Trans } from "@lingui/react";

interface FilterComponentProps {
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  onClear: () => void;
  facetedValues?: Map<unknown, number>;
  minMaxValues?: [unknown, unknown];
  selectOptions?: Array<{ value: string; label: string }>;
  columnLabel?: string;
}

/**
 * Select filter component using EnhancedMultiSelect
 * Shows faceted values with counts when available
 */
export function SelectFilter({
  value,
  onChange,
  onClear,
  facetedValues,
  selectOptions = [],
  columnLabel,
}: FilterComponentProps) {
  const { t } = useLingui();

  const options = React.useMemo(() => {
    if (selectOptions.length > 0) {
      // Use predefined options as the source of truth, with counts from faceted values
      return selectOptions.map((opt) => {
        const count = facetedValues?.get(opt.value) || 0;
        return { value: opt.value, label: `${opt.label} (${count})` };
      });
    }
    // Fallback: derive options from faceted values when no predefined options exist
    return Array.from(facetedValues?.entries() || [])
      .map(([val, count]) => {
        const stringValue = String(val);
        return { value: stringValue, label: `${stringValue} (${count})` };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [facetedValues, selectOptions]);

  const selectedValues = Array.isArray(value) ? value : [];

  return (
    <div className="p-3 space-y-2">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-medium">
          {columnLabel ? (
            <Trans id="table.filter.selectValuesFor" values={{ columnLabel }}>
              Select values for {columnLabel}
            </Trans>
          ) : (
            t`Select values`
          )}
        </Label>
        {selectedValues.length > 0 && (
          <Button
            variant="ghost"
            onClick={onClear}
            className="h-auto px-2 py-1 text-xs"
          >
            {t`Clear`}
          </Button>
        )}
      </div>
      <EnhancedMultiSelect
        value={selectedValues}
        onChange={(newValues) => onChange(newValues)}
        options={options}
        placeholder={t`Select...`}
        className="w-full"
      />
    </div>
  );
}

/**
 * Date range filter component
 * Allows filtering by date ranges using DatePicker components
 */
export function DateRangeFilter({
  value,
  onChange,
  onClear,
  columnLabel,
}: FilterComponentProps) {
  const { t } = useLingui();
  const dateValue =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as { from?: Date; to?: Date })
      : {};

  const hasValue = dateValue.from || dateValue.to;

  return (
    <div className="p-3 space-y-2">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-medium">
          {columnLabel ? (
            <Trans id="table.filter.selectDatesFor" values={{ columnLabel }}>
              Select dates for {columnLabel}
            </Trans>
          ) : (
            t`Select dates`
          )}
        </Label>
        {hasValue && (
          <Button
            variant="ghost"
            onClick={onClear}
            className="h-auto px-2 py-1 text-xs"
          >
            {t`Clear`}
          </Button>
        )}
      </div>
      <div className="flex w-full items-center gap-2">
        <DatePicker
          value={dateValue.from}
          onChange={(date) => onChange({ ...dateValue, from: date })}
          placeholder={t`From`}
          className="flex-1"
        />
        <DatePicker
          value={dateValue.to}
          onChange={(date) => onChange({ ...dateValue, to: date })}
          placeholder={t`To`}
          className="flex-1"
        />
      </div>
    </div>
  );
}
